import {
    useContext,
    useEffect,
    useState
} from "react";
import {
    TextInput as NativeTextInput,
    useWindowDimensions,
    TouchableOpacity,
    ScrollView,
    Image,
    View
} from "react-native";
import type {
    Organization,
    SuperAdmin
} from "./views/superAdminFormModal/type";
import stylesheet from "./stylesheet";
import SuperAdminFormModal from "./views/superAdminFormModal";
import SuperAdminPreviewModal from "./views/superAdminPreviewModal";
import {
    NCoreUIKitLocalize,
    NCoreUIKitDialog,
    NCoreUIKitToast,
    NCoreUIKitModal,
    NCoreUIKitTheme,
    PageContainer,
    StateCard,
    TextInput,
    Button,
    Text
} from "ncore-ui-kit";
import {
    ChevronRightIcon,
    ChevronLeftIcon,
    RefreshCcwIcon,
    UserCogIcon,
    PencilIcon,
    TrashIcon,
    UserIcon,
    PlusIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../../../contexts/AuthContext";

const RootSuperAdmins = () => {
    const [
        organizations,
        setOrganizations
    ] = useState<Organization[]>([]);
    const [
        superAdmins,
        setSuperAdmins
    ] = useState<SuperAdmin[]>([]);

    const [
        debouncedSearch,
        setDebouncedSearch
    ] = useState<string>("");

    const [
        jumpPage,
        setJumpPage
    ] = useState<string>("");

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState<boolean>(false);

    const [
        search,
        setSearch
    ] = useState<string>("");

    const [
        totalPages,
        setTotalPages
    ] = useState<number>(1);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        user
    } = useContext(AuthContext);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [search]);

    const fetchData = async () => {
        try {
            const token = await AsyncStorage.getItem("authToken");

            const [
                superAdminsRes,
                orgsRes
            ] = await Promise.all([
                fetch(`${process.env.API_URL}/users/super-admins?page=${page}&limit=10&search=${encodeURIComponent(debouncedSearch)}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    method: "GET"
                }),
                fetch(`${process.env.API_URL}/organizations?limit=1000`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    method: "GET"
                })
            ]);

            const superAdminsData = await superAdminsRes.json();
            const orgsData = await orgsRes.json();

            if (superAdminsData.status === "success") {
                const dataArray = Array.isArray(superAdminsData.data)
                    ? superAdminsData.data
                    : (Array.isArray(superAdminsData.data?.data) ? superAdminsData.data.data : []);
                setSuperAdmins(dataArray);

                const meta = superAdminsData.meta || superAdminsData.data?.meta;
                if (meta && meta.totalPages) {
                    setTotalPages(meta.totalPages);
                } else {
                    setTotalPages(1);
                }
            }

            if (orgsData.status === "success") {
                const orgsArray = Array.isArray(orgsData.data)
                    ? orgsData.data
                    : (Array.isArray(orgsData.data?.data) ? orgsData.data.data : []);
                setOrganizations(orgsArray);
            }
        } catch {
            NCoreUIKitToast.open({
                title: localize("superadmin-toast-fetch-failed")
            });
        }
    };

    useEffect(() => {
        fetchData();
    }, [
        page,
        debouncedSearch
    ]);

    const openFormModal = (admin?: SuperAdmin) => {
        const modalId = "superadmin-form-modal";
        NCoreUIKitModal.open({
            children: (
                <SuperAdminFormModal
                    organizations={organizations}
                    onSuccess={fetchData}
                    initialData={admin}
                    modalId={modalId}
                />),
            onOverlayPress: ({
                closeAnimation
            }) => closeAnimation(),
            id: modalId
        });
    };

    const openPreviewModal = (admin: SuperAdmin, orgName: string) => {
        const modalId = "superadmin-preview-modal";
        NCoreUIKitModal.open({
            children: (
                <SuperAdminPreviewModal
                    orgName={orgName}
                    modalId={modalId}
                    admin={admin}
                />),
            onOverlayPress: ({
                closeAnimation
            }) => closeAnimation(),
            id: modalId
        });
    };

    const handleDelete = (id: string, username: string) => {
        let isDeleting = false;

        NCoreUIKitDialog.open({
            primaryButtonProps: {
                onPress: async ({
                    closeAnimation
                }) => {
                    if (isDeleting) return;

                    isDeleting = true;

                    try {
                        const token = await AsyncStorage.getItem("authToken");
                        const response = await fetch(`${process.env.API_URL}/users/${id}`, {
                            headers: {
                                Authorization: `Bearer ${token}`
                            },
                            method: "DELETE"
                        });
                        const data = await response.json();
                        if (data.status === "success") {
                            NCoreUIKitToast.open({
                                title: localize("superadmin-toast-delete-success")
                            });
                            fetchData();
                        } else {
                            NCoreUIKitToast.open({
                                title: data.message || localize("superadmin-toast-delete-error")
                            });
                        }
                    } catch {
                        NCoreUIKitToast.open({
                            title: localize("superadmin-toast-server-error")
                        });
                    } finally {
                        isDeleting = false;

                        closeAnimation();
                    }
                },
                title: localize("superadmin-dialog-delete-primary"),
                type: "danger"
            },
            content: localize("superadmin-dialog-delete-content", [
                username
            ]),
            secondaryButtonProps: {
                title: localize("superadmin-dialog-delete-cancel"),
                onPress: ({
                    closeAnimation
                }) => {
                    closeAnimation();
                },
                type: "neutral"
            },
            title: localize("superadmin-dialog-delete-title"),
            variant: "yes-no",
            isVisible: true
        });
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.header,
                    {
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? spaces.spacingMd : 0,
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("superadmin-page-title")}
                </Text>
                <View
                    style={[
                        stylesheet.headerButtonsContainer,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <RefreshCcwIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        onPress={async () => {
                            setIsRefreshing(true);
                            await fetchData();
                            setIsRefreshing(false);
                        }}
                        isLoading={isRefreshing}
                        spreadBehaviour="free"
                        variant="ghost"
                        type="neutral"
                    />
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <PlusIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        title={localize("superadmin-page-add-button")}
                        onPress={() => openFormModal()}
                        color="primary"
                    />
                </View>
            </View>
            <View
                style={[
                    stylesheet.searchContainer,
                    {
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <TextInput
                    placeholder={localize("superadmin-search-placeholder")}
                    spreadBehaviour="stretch"
                    onChangeText={setSearch}
                    value={search}
                />
            </View>
            <View
                style={[
                    stylesheet.listContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <ScrollView
                    contentContainerStyle={[
                        stylesheet.scrollContent,
                        {
                            paddingBottom: spaces.spacingXl,
                            gap: spaces.spacingMd
                        }
                    ]}
                    showsVerticalScrollIndicator={false}
                >
                    {
                        superAdmins.length === 0 ?
                            <StateCard
                                subTitle={debouncedSearch ? localize("superadmin-list-search-empty-subtitle") : localize("superadmin-list-empty-subtitle")}
                                title={debouncedSearch ? localize("superadmin-list-search-empty-title") : localize("superadmin-list-empty-title")}
                                icon={({
                                    color
                                }) => {
                                    return <UserCogIcon
                                        color={colors.content.icon[color]}
                                    />;
                                }}
                                type="neutral"
                            />
                        :
                            superAdmins.map((admin) => {
                                const orgsArray = Array.isArray(admin.organization_ids)
                                    ? admin.organization_ids
                                    : (admin.organization_id ? [
                                        admin.organization_id
                                    ] : []);

                                const orgName = orgsArray.length > 0
                                    ? orgsArray.map((id: string) => organizations.find((org) => org.id === id)?.name || localize("superadmin-list-unknown-org")).join(", ")
                                    : localize("superadmin-list-unassigned-org");

                                return <TouchableOpacity
                                    style={[
                                        stylesheet.card,
                                        {
                                            backgroundColor: colors.content.container.mid,
                                            flexDirection: isMobile ? "column" : "row",
                                            borderRadius: radiuses.md,
                                            padding: spaces.spacingMd,
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                    onPress={() => openPreviewModal(admin, orgName)}
                                    activeOpacity={0.7}
                                    key={admin.id}
                                >
                                    <View
                                        style={[
                                            stylesheet.cardContent,
                                            {
                                                width: isMobile ? "100%" : "auto",
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                    >
                                        {
                                            admin.profilePicturePath ?
                                                <Image
                                                    style={[
                                                        stylesheet.profileImage,
                                                        {
                                                            borderRadius: radiuses.full
                                                        }
                                                    ]}
                                                    source={{
                                                        uri: admin.profilePicturePath
                                                    }}
                                                />
                                            :
                                                <View
                                                    style={[
                                                        stylesheet.profileImage,
                                                        {
                                                            backgroundColor: colors.content.container.default,
                                                            borderRadius: radiuses.full,
                                                            justifyContent: "center",
                                                            alignItems: "center"
                                                        }
                                                    ]}
                                                >
                                                    <UserIcon
                                                        color={colors.content.icon.emphasized}
                                                        size={20}
                                                    />
                                                </View>
                                        }
                                        <View
                                            style={[
                                                stylesheet.cardInfo,
                                                {
                                                    gap: spaces.spacingSm
                                                }
                                            ]}
                                        >
                                            <Text
                                                variant="titleMediumSize"
                                            >
                                                {admin.username} ({admin.email})
                                            </Text>
                                            <Text
                                                variant="bodyMediumSize"
                                            >
                                                {localize("superadmin-list-org-label", [
                                                    orgName
                                                ])}
                                            </Text>
                                        </View>
                                    </View>
                                    <View
                                        style={[
                                            stylesheet.cardActions,
                                            {
                                                flexDirection: isMobile ? "column" : "row",
                                                width: isMobile ? "100%" : "auto",
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                    >
                                        <Button
                                            icon={({
                                                color,
                                                size
                                            }) => {
                                                return <PencilIcon
                                                    color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                    size={size}
                                                />;
                                            }}
                                            title={localize("superadmin-list-edit-button")}
                                            spreadBehaviour={isMobile ? "stretch" : "free"}
                                            onPress={() => openFormModal(admin)}
                                            type="neutral"
                                        />
                                        {admin.role !== "root" && admin.id !== user?.id && <Button
                                            icon={({
                                                color,
                                                size
                                            }) => {
                                                return <TrashIcon
                                                    color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                    size={size}
                                                />;
                                            }}
                                            onPress={() => handleDelete(admin.id, admin.username)}
                                            title={localize("superadmin-list-delete-button")}
                                            spreadBehaviour={isMobile ? "stretch" : "free"}
                                            type="danger"
                                        />}
                                    </View>
                                </TouchableOpacity>;
                            })
                    }
                </ScrollView>
                {
                    totalPages > 1 && (
                        <View
                            style={[
                                stylesheet.paginationContainer,
                                {
                                    flexDirection: isMobile ? "column" : "row",
                                    paddingTop: spaces.spacingMd,
                                    gap: spaces.spacingXl
                                }
                            ]}
                        >
                            <Button
                                icon={({
                                    color,
                                    size
                                }) => {
                                    return <ChevronLeftIcon
                                        color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                        size={size}
                                    />;
                                }}
                                onPress={() => setPage(p => Math.max(1, p - 1))}
                                title={localize("superadmin-pagination-prev")}
                                isDisabled={page === 1}
                                color="secondary"
                            />
                            <Text
                                variant="bodyMediumSize"
                            >
                                {page} / {totalPages}
                            </Text>
                            <Button
                                icon={({
                                    color,
                                    size
                                }) => {
                                    return <ChevronRightIcon
                                        color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                        size={size}
                                    />;
                                }}
                                onPress={() => setPage(p => Math.min(totalPages, p + 1))}
                                title={localize("superadmin-pagination-next")}
                                isDisabled={page === totalPages}
                                iconDirection="right"
                                color="secondary"
                            />
                            <View
                                style={[
                                    stylesheet.jumpContainer,
                                    {
                                        marginLeft: spaces.spacingMd,
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <View
                                    style={stylesheet.jumpInputContainer}
                                >
                                    <NativeTextInput
                                        onChangeText={(text) => setJumpPage(text.replace(/[^0-9]/g, ""))}
                                        placeholder={localize("superadmin-pagination-jump-placeholder")}
                                        style={[
                                            stylesheet.jumpInput,
                                            {
                                                borderColor: colors.content.border.subtle,
                                                paddingHorizontal: spaces.spacingMd,
                                                color: colors.content.text.high,
                                                borderRadius: radiuses.md
                                            }
                                        ]}
                                        placeholderTextColor={colors.content.text.low}
                                        keyboardType="numeric"
                                        value={jumpPage}
                                    />
                                </View>
                                <Button
                                    title={localize("superadmin-pagination-jump-button")}
                                    onPress={() => {
                                        const p = parseInt(jumpPage, 10);
                                        if (p >= 1 && p <= totalPages) {
                                            setPage(p);
                                        }
                                        setJumpPage("");
                                    }}
                                    isDisabled={!jumpPage}
                                    color="secondary"
                                />
                            </View>
                        </View>)
                }
            </View>
        </View>
    </PageContainer>;
};

export default RootSuperAdmins;
